import { useFormik } from 'formik';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Textarea } from 'rizzui';
import * as yup from 'yup';
import RequiredInput from '../../components/forms/InputHOC';
import { PermissionsGrid } from '../../components/ui/PermissionsAccordion';
import useFetch from '../../hooks/useFetch';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { PermissionService } from '../../services/auth/permissionService';
import { PermissionGroup, PermissionsApiResponse, Role, RolePermission } from '../../type/entities/role-type';
import FormFooter from '../_layout/_footers/form-footer';

interface CreateFormProps {
    isLoading: boolean;
    submit: Function;
    data?: Role | undefined;
}

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
    description: yup.string().nullable(),
});

const RoleForm: React.FC<CreateFormProps> = ({ isLoading, submit, data = undefined }) => {

    // STATES

    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);

    // HOOKS

    const navigate = useNavigate();
    const mode = data ? 'Editar' : 'Crear';

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER LOS PERMISOS
     * @EN GET PERMISSIONS
     */
    //--------------------------------------------------------------------------------------------------------------
    const [permissionsData] = useFetch(useCallback(async () => {
        const response = await (new PermissionService()).getPermissions();
        return response.getResponseData() as PermissionsApiResponse;
    }, []));
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR LOS PERMISOS SELECCIONADOS
     * @EN HANDLE SELECTED PERMISSIONS
     */
    //--------------------------------------------------------------------------------------------------------------
    const _handleSelectedPermissions = (permissions: RolePermission[]) => {
        let permissionsIds = permissions.map((permission: RolePermission) => permission.permission.id);
        setSelectedPermissions(permissionsIds);
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR LA SELECCION DE TODOS LOS PERMISOS
     * @EN HANDLE SELECT ALL PERMISSIONS
     */
    //--------------------------------------------------------------------------------------------------------------
    const _handleSelectAll = (permissions: number[], checked: boolean) => {
        if (checked && selectedPermissions !== null) {
            // if already in selectedPermissions, do nothing
            let permissionsToAdd: number[] = [];
            permissions.forEach((permission: number) => {
                if (!selectedPermissions.includes(permission)) {
                    permissionsToAdd.push(permission);
                }
            });
            setSelectedPermissions([...selectedPermissions, ...permissionsToAdd]);
        }

        if (!checked && selectedPermissions !== null) {
            const selectedPermissionsCopy = selectedPermissions;

            permissions.forEach((permission: number) => {
                const index = selectedPermissionsCopy.indexOf(permission);
                if (index > -1) {
                    selectedPermissionsCopy.splice(index, 1);
                }
            });

            setSelectedPermissions([...selectedPermissionsCopy]);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR LA SELECCION DE UN PERMISO
     * @EN HANDLE SELECT PERMISSION
     */
    //--------------------------------------------------------------------------------------------------------------
    const _handleOnSelectPermission = (permission: number, checked: boolean) => {
        if (checked && selectedPermissions !== null) {
            if (!selectedPermissions.includes(permission)) {
                setSelectedPermissions([...selectedPermissions, permission]);
            }
        }

        if (!checked && selectedPermissions !== null) {
            const selectedPermissionsCopy = selectedPermissions;
            const index = selectedPermissionsCopy.indexOf(permission);
            if (index > -1) {
                selectedPermissionsCopy.splice(index, 1);
            }
            setSelectedPermissions([...selectedPermissionsCopy]);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: data?.id ?? '',
            name: data?.name ?? '',
            description: data?.description ?? '',
            permissions: data?.permissions.map((permission: RolePermission) => permission.permission.id) ?? []
        },
        validationSchema: schema,
        validateOnBlur: false,
        // validateOnChange: false,
        onSubmit: values => { submit(values, selectedPermissions); },
    });

    // USE EFFECTS

    useEffect(() => {
        if (data) _handleSelectedPermissions(data.permissions);
    }, [data]);

    // RENDER FUNCTIONS

    const getContent = () => {
        return (
            <>
                <div className="@container">
                    <form onSubmit={formik.handleSubmit} autoComplete="off">
                        <div className="mb-10 grid gap-7 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11 font-medium">
                            <FormGroup
                                title="Información General"
                                description="Datos principales del rol"
                                className='pt-6'
                            >
                                <RequiredInput
                                    id='name'
                                    label="Nombre"
                                    formik={formik}
                                />

                                <Textarea
                                    id='description'
                                    label="Descripción"
                                    required
                                    className="[&>label>span]:font-medium col-span-2"
                                    rows={3}
                                    onChange={formik.handleChange}
                                    value={formik.values.description}
                                    error={formik.errors.description}
                                />
                            </FormGroup>

                            {selectedPermissions !== null && permissionsData &&
                                permissionsData.map((permissionGroup: PermissionGroup, index: number) => {
                                    return (
                                        <FormGroup
                                            key={`index-${permissionGroup.id}`}
                                            title={permissionGroup.label}
                                            description={'Lista de permisos asignados al usuario'}
                                            className='pt-6'
                                            childClassName='col-span-full grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 @4xl:col-span-8 @4xl:gap-5 xl:gap-7'
                                        >
                                            <div key={permissionGroup.id} className={"col-span-full"}>
                                                <PermissionsGrid
                                                    group={permissionGroup}
                                                    onSelectAll={_handleSelectAll}
                                                    onSelectPermission={_handleOnSelectPermission}
                                                    selectedPermissions={selectedPermissions}
                                                />
                                            </div>
                                        </FormGroup>
                                    )
                                })
                            }
                        </div>
                    </form>
                </div>
            </>
        )
    };

    return (
        <Fragment>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Rol'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm} isLoading={isLoading}
            />
        </Fragment>
    )
};

export default RoleForm;